import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Avatar,
    Box,
    Button,
    Chip,
    Container,
    Divider,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography
} from '@mui/material';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { alpha, useTheme } from '@mui/material/styles';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import StorageIcon from '@mui/icons-material/Storage';
import TimelineIcon from '@mui/icons-material/Timeline';
import SpeedIcon from '@mui/icons-material/Speed';
import LanguageIcon from '@mui/icons-material/Language';
import FilterListIcon from '@mui/icons-material/FilterList';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SaveIcon from '@mui/icons-material/Save';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const STATUSES = ['completed', 'in-progress', 'pending'];
const STATUS_LABELS = {
    completed: 'Completed',
    'in-progress': 'In progress',
    pending: 'Pending'
};

const frequencyDeliveryOptions = {
    weekly: [
        { value: 'monday', label: 'Mondays' },
        { value: 'wednesday', label: 'Wednesdays' },
        { value: 'friday', label: 'Fridays' },
        { value: 'sunday', label: 'Sundays' }
    ],
    monthly: [
        { value: '1', label: '1st of the month' },
        { value: '15', label: '15th of the month' },
        { value: 'last', label: 'Last day of the month' }
    ],
    quarterly: [
        { value: 'start', label: 'Start of each quarter' },
        { value: 'mid', label: 'Mid-quarter summary' },
        { value: 'end', label: 'Quarter wrap-up' }
    ],
    yearly: [
        { value: 'january', label: 'Every January' },
        { value: 'april', label: 'Every April' },
        { value: 'july', label: 'Every July' },
        { value: 'october', label: 'Every October' },
        { value: 'december', label: 'Every December' }
    ]
};

const frequencyLabels = {
    weekly: 'Weekly',
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    yearly: 'Yearly'
};

const summaryTimeRangeOptions = [
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 90 days' },
    { value: '180d', label: 'Last 6 months' },
    { value: '365d', label: 'Last 12 months' },
    { value: 'all', label: 'All time' }
];

const statusTimeRangeOptions = [
    { value: '90d', label: 'Last 90 days' },
    { value: '180d', label: 'Last 6 months' },
    { value: '365d', label: 'Last 12 months' },
    { value: 'all', label: 'All time' }
];

const tableTimeRangeOptions = [
    { value: '30d', label: '30 days' },
    { value: '90d', label: '90 days' },
    { value: '180d', label: '6 months' },
    { value: '365d', label: '12 months' },
    { value: 'all', label: 'All time' }
];

const volumeRangeOptions = [
    { value: '3m', label: '3M' },
    { value: '6m', label: '6M' },
    { value: '12m', label: '12M' },
    { value: 'all', label: 'All' }
];

const tableSortOptions = [
    { value: 'recent', label: 'Most recent' },
    { value: 'progress', label: 'Progress' },
    { value: 'words', label: 'Word count' }
];

const describeTimeRange = (range) => {
    const option = [...summaryTimeRangeOptions, ...statusTimeRangeOptions, ...tableTimeRangeOptions].find(
        (item) => item.value === range
    );
    return option ? option.label : 'All time';
};

const parseTimeRangeThreshold = (range) => {
    if (!range || range === 'all') {
        return null;
    }
    const match = range.match(/^(\d+)([dmy])$/);
    if (!match) {
        return null;
    }
    const value = Number(match[1]);
    const unit = match[2];
    const threshold = new Date();
    if (Number.isNaN(value)) {
        return null;
    }
    if (unit === 'd') {
        threshold.setDate(threshold.getDate() - value);
    } else if (unit === 'm') {
        threshold.setMonth(threshold.getMonth() - value);
    } else if (unit === 'y') {
        threshold.setFullYear(threshold.getFullYear() - value);
    }
    return threshold;
};

const applyTimeRangeFilter = (projects, range) => {
    const threshold = parseTimeRangeThreshold(range);
    if (!threshold) {
        return projects;
    }
    return projects.filter((project) => {
        if (!project.timestamp) {
            return false;
        }
        const projectDate = new Date(project.timestamp);
        if (Number.isNaN(projectDate.getTime())) {
            return false;
        }
        return projectDate >= threshold;
    });
};

const normalizeStatus = (status) => {
    if (typeof status !== 'string') {
        return 'pending';
    }
    const normalized = status.toLowerCase();
    return STATUSES.includes(normalized) ? normalized : 'pending';
};

const getDeliveryLabel = (frequency, value) => {
    const options = frequencyDeliveryOptions[frequency] || [];
    const match = options.find((option) => option.value === value);
    return match ? match.label : '';
};

export default function AnalyticsPage() {
    const token = useSelector((state) => state.auth.token);
    const [stats, setStats] = useState(null);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [reportFeedback, setReportFeedback] = useState(null);
    const theme = useTheme();

    const [summaryFilters, setSummaryFilters] = useState({ status: 'all', timeRange: '365d' });
    const [volumeFilters, setVolumeFilters] = useState({ range: '6m' });
    const [statusSectionFilters, setStatusSectionFilters] = useState({ statuses: STATUSES, timeRange: '365d' });
    const [tableFilters, setTableFilters] = useState({ status: 'all', timeRange: 'all', search: '', sort: 'recent' });
    const [languageFilter, setLanguageFilter] = useState({ limit: 5 });
    const [reportConfig, setReportConfig] = useState({
        frequency: 'weekly',
        deliveryOption: frequencyDeliveryOptions.weekly[0].value,
        sendTime: '09:00',
        recipients: '',
        includeSections: {
            summary: true,
            volume: true,
            status: true,
            languages: true,
            insights: true,
            table: true
        },
        includeFilters: true,
        attachCsv: true,
        customMessage: ''
    });
    const [savingReport, setSavingReport] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [statsRes, projectsRes] = await Promise.all([
                    axios.get('/api/stats', { headers: { Authorization: `Bearer ${token}` } }),
                    axios.get('/api/projects', { headers: { Authorization: `Bearer ${token}` } })
                ]);
                setStats(statsRes.data);
                const allProjects = [
                    ...(projectsRes.data.createdProjects || []),
                    ...(projectsRes.data.sharedProjects || [])
                ];
                setProjects(allProjects);
            } catch (err) {
                setError(err.response?.data?.msg || 'Failed to load analytics');
            } finally {
                setLoading(false);
            }
        };
        if (token) {
            fetchData();
        }
    }, [token]);

    useEffect(() => {
        const options = frequencyDeliveryOptions[reportConfig.frequency] || [];
        if (options.length && !options.some((option) => option.value === reportConfig.deliveryOption)) {
            setReportConfig((prev) => ({
                ...prev,
                deliveryOption: options[0].value
            }));
        }
    }, [reportConfig.frequency, reportConfig.deliveryOption]);

    const {
        translationMemoryCount = 0,
        topLanguages = []
    } = stats || {};

    const projectMetrics = useMemo(
        () =>
            (projects || []).map((project) => {
                const sections = Array.isArray(project.sections) ? project.sections : [];
                const translatedSections = Array.isArray(project.translatedSections)
                    ? project.translatedSections.filter((section) => typeof section === 'string' && section.trim().length > 0)
                    : [];
                const totalSections = sections.length;
                const translatedCount = translatedSections.length;
                const progress = totalSections ? Math.round((translatedCount / totalSections) * 100) : 0;
                const wordCount = sections.reduce((sum, section) => {
                    if (typeof section !== 'string') {
                        return sum;
                    }
                    return sum + section.split(/\s+/).filter(Boolean).length;
                }, 0);
                const timestamp = project.updatedAt || project.createdAt || null;
                const derivedStatus = progress === 100 ? 'completed' : progress > 0 ? 'in-progress' : 'pending';

                return {
                    id: project._id,
                    name: project.projectName,
                    progress,
                    wordCount,
                    timestamp,
                    status: normalizeStatus(project.status) || derivedStatus
                };
            }),
        [projects]
    );

    const summaryFilteredProjects = useMemo(() => {
        let filtered = applyTimeRangeFilter(projectMetrics, summaryFilters.timeRange);
        if (summaryFilters.status !== 'all') {
            filtered = filtered.filter((project) => normalizeStatus(project.status) === summaryFilters.status);
        }
        return filtered;
    }, [projectMetrics, summaryFilters]);

    const summaryStats = useMemo(() => {
        const total = summaryFilteredProjects.length;
        const completedCount = summaryFilteredProjects.filter((project) => normalizeStatus(project.status) === 'completed').length;
        const inProgressCount = summaryFilteredProjects.filter(
            (project) => normalizeStatus(project.status) === 'in-progress'
        ).length;
        const estimatedWords = summaryFilteredProjects.reduce(
            (sum, project) => sum + Math.round((project.wordCount * project.progress) / 100),
            0
        );
        const totalWords = summaryFilteredProjects.reduce((sum, project) => sum + project.wordCount, 0);
        const averageProgress = total
            ? Math.round(summaryFilteredProjects.reduce((sum, project) => sum + project.progress, 0) / total)
            : 0;
        const averageWords = total ? Math.round(totalWords / total) : 0;

        return {
            total,
            completedCount,
            inProgressCount,
            estimatedWords,
            totalWords,
            averageProgress,
            averageWords
        };
    }, [summaryFilteredProjects]);

    const projectsForVolume = useMemo(
        () => applyTimeRangeFilter(projectMetrics, volumeFilters.range === 'all' ? 'all' : volumeFilters.range),
        [projectMetrics, volumeFilters.range]
    );

    const monthlyWordData = useMemo(() => {
        if (!projectsForVolume.length) {
            return [];
        }
        const monthMap = new Map();
        projectsForVolume.forEach((metric) => {
            if (!metric.timestamp) {
                return;
            }
            const date = new Date(metric.timestamp);
            if (Number.isNaN(date.getTime())) {
                return;
            }
            const monthKey = `${date.getFullYear()}-${date.getMonth()}`;
            const monthLabel = date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
            const monthStart = new Date(date.getFullYear(), date.getMonth(), 1).getTime();
            const existing = monthMap.get(monthKey);
            if (existing) {
                existing.value += metric.wordCount;
            } else {
                monthMap.set(monthKey, { label: monthLabel, value: metric.wordCount, sortValue: monthStart });
            }
        });
        return Array.from(monthMap.values()).sort((a, b) => a.sortValue - b.sortValue);
    }, [projectsForVolume]);

    const monthlyTrendMax = monthlyWordData.reduce((max, entry) => Math.max(max, entry.value), 0);
    const latestMonthWords = monthlyWordData.length ? monthlyWordData[monthlyWordData.length - 1].value : 0;

    const statusesToDisplay = statusSectionFilters.statuses.length ? statusSectionFilters.statuses : STATUSES;

    const filteredStatusProjects = useMemo(() => {
        let filtered = applyTimeRangeFilter(projectMetrics, statusSectionFilters.timeRange);
        if (statusSectionFilters.statuses.length) {
            filtered = filtered.filter((project) => statusesToDisplay.includes(normalizeStatus(project.status)));
        }
        return filtered;
    }, [projectMetrics, statusSectionFilters.timeRange, statusSectionFilters.statuses, statusesToDisplay]);

    const statusBreakdown = useMemo(() => {
        const counts = filteredStatusProjects.reduce(
            (acc, metric) => {
                const statusKey = normalizeStatus(metric.status);
                acc[statusKey] = (acc[statusKey] || 0) + 1;
                return acc;
            },
            { completed: 0, 'in-progress': 0, pending: 0 }
        );

        const entries = STATUSES.map((status) => ({
            status,
            label: STATUS_LABELS[status],
            color:
                status === 'completed'
                    ? theme.palette.success.main
                    : status === 'in-progress'
                        ? theme.palette.warning.main
                        : theme.palette.info.main,
            count: counts[status] || 0
        })).filter((entry) => statusesToDisplay.includes(entry.status));

        const totalCount = entries.reduce((sum, entry) => sum + entry.count, 0);

        let currentAngle = 0;
        const gradientSegments = entries
            .map((entry) => {
                if (!entry.count || !totalCount) {
                    return null;
                }
                const startAngle = currentAngle;
                const angle = (entry.count / totalCount) * 360;
                currentAngle += angle;
                const endAngle = startAngle + angle;
                return `${entry.color} ${startAngle}deg ${endAngle}deg`;
            })
            .filter(Boolean)
            .join(', ');

        return {
            entries,
            totalCount,
            gradient: gradientSegments
        };
    }, [filteredStatusProjects, theme.palette.info.main, theme.palette.success.main, theme.palette.warning.main, statusesToDisplay]);

    const tableProjects = useMemo(() => {
        let filtered = applyTimeRangeFilter(projectMetrics, tableFilters.timeRange);
        if (tableFilters.status !== 'all') {
            filtered = filtered.filter((project) => normalizeStatus(project.status) === tableFilters.status);
        }
        if (tableFilters.search.trim()) {
            const query = tableFilters.search.trim().toLowerCase();
            filtered = filtered.filter((project) => project.name?.toLowerCase().includes(query));
        }
        const sorted = [...filtered];
        if (tableFilters.sort === 'progress') {
            sorted.sort((a, b) => b.progress - a.progress || b.wordCount - a.wordCount);
        } else if (tableFilters.sort === 'words') {
            sorted.sort((a, b) => b.wordCount - a.wordCount);
        } else {
            sorted.sort((a, b) => {
                const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
                const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;
                return bTime - aTime;
            });
        }
        return sorted.slice(0, 10);
    }, [projectMetrics, tableFilters]);

    const displayedLanguages = useMemo(
        () => (topLanguages || []).slice(0, languageFilter.limit || 5),
        [topLanguages, languageFilter.limit]
    );

    const maxLanguageCount = useMemo(
        () => displayedLanguages.reduce((max, item) => Math.max(max, item.count || 0), 0),
        [displayedLanguages]
    );

    const heroGradient = `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`;

    const summaryCards = useMemo(
        () => [
            {
                key: 'words',
                label: 'Estimated words translated',
                value: summaryStats.estimatedWords,
                sublabel:
                    summaryStats.total > 0
                        ? `Based on ${summaryStats.total} filtered project${summaryStats.total === 1 ? '' : 's'}`
                        : 'No projects match the current filters',
                icon: <TrendingUpIcon fontSize="large" />,
                color: theme.palette.primary.main
            },
            {
                key: 'completion',
                label: 'Completion rate',
                value: summaryStats.total
                    ? Math.round((summaryStats.completedCount / summaryStats.total) * 100)
                    : 0,
                sublabel: `${summaryStats.completedCount} of ${summaryStats.total || 0} filtered projects`,
                icon: <AssignmentTurnedInIcon fontSize="large" />,
                color: theme.palette.success.main,
                valueSuffix: '%'
            },
            {
                key: 'active',
                label: 'Active projects',
                value: summaryStats.inProgressCount,
                sublabel: `${summaryStats.inProgressCount} currently in motion`,
                icon: <PendingActionsIcon fontSize="large" />,
                color: theme.palette.warning.main
            },
            {
                key: 'tm',
                label: 'TM entries',
                value: translationMemoryCount,
                sublabel: 'Global translation memory units',
                icon: <StorageIcon fontSize="large" />,
                color: theme.palette.info.main,
                isGlobal: true
            }
        ],
        [
            summaryStats.completedCount,
            summaryStats.estimatedWords,
            summaryStats.inProgressCount,
            summaryStats.total,
            theme.palette.info.main,
            theme.palette.primary.main,
            theme.palette.success.main,
            theme.palette.warning.main,
            translationMemoryCount
        ]
    );

    const scheduleSummary = useMemo(() => {
        const frequencyLabel = frequencyLabels[reportConfig.frequency];
        const deliveryLabel = getDeliveryLabel(reportConfig.frequency, reportConfig.deliveryOption);
        return `${frequencyLabel || 'Custom'} report • ${deliveryLabel || 'Custom cadence'} at ${reportConfig.sendTime}`;
    }, [reportConfig.frequency, reportConfig.deliveryOption, reportConfig.sendTime]);

    const recipientCount = useMemo(() => {
        if (!reportConfig.recipients.trim()) {
            return 0;
        }
        return reportConfig.recipients
            .split(',')
            .map((entry) => entry.trim())
            .filter(Boolean).length;
    }, [reportConfig.recipients]);

    const statusToChipColor = {
        completed: 'success',
        'in-progress': 'warning',
        pending: 'info'
    };

    const sectionsForReport = [
        { key: 'summary', label: 'Summary cards' },
        { key: 'volume', label: 'Monthly volume chart' },
        { key: 'status', label: 'Status breakdown' },
        { key: 'languages', label: 'Top languages' },
        { key: 'insights', label: 'Project insights' },
        { key: 'table', label: 'Project progress table' }
    ];

    const handleReportSave = (event) => {
        event.preventDefault();
        setSavingReport(true);
        setReportFeedback(null);
        setTimeout(() => {
            setSavingReport(false);
            setReportFeedback({
                severity: 'success',
                message: 'Report configuration saved. Email delivery will use these settings when automated reporting is connected.'
            });
        }, 600);
    };

    const handleExportTable = () => {
        if (typeof window === 'undefined' || !tableProjects.length) {
            return;
        }
        const header = ['Project', 'Progress (%)', 'Status', 'Words', 'Last updated'];
        const rows = tableProjects.map((project) => [
            project.name || 'Untitled project',
            project.progress,
            STATUS_LABELS[normalizeStatus(project.status)],
            project.wordCount,
            project.timestamp ? new Date(project.timestamp).toLocaleDateString() : 'No date'
        ]);
        const csv = [header, ...rows]
            .map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(','))
            .join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'project-analytics.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const resetSummaryFilters = () => setSummaryFilters({ status: 'all', timeRange: '365d' });
    const resetStatusFilters = () => setStatusSectionFilters({ statuses: STATUSES, timeRange: '365d' });
    const resetTableFilters = () => setTableFilters({ status: 'all', timeRange: 'all', search: '', sort: 'recent' });

    if (loading) {
        return (
            <Container sx={{ mt: 4 }}>
                <Typography>Loading...</Typography>
            </Container>
        );
    }

    if (error) {
        return (
            <Container sx={{ mt: 4 }}>
                <Typography color="error">{error}</Typography>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Box
                sx={{
                    borderRadius: 4,
                    background: heroGradient,
                    color: theme.palette.common.white,
                    p: { xs: 3, md: 5 },
                    mb: 4,
                    boxShadow: '0 24px 40px rgba(33, 150, 243, 0.28)'
                }}
            >
                <Typography variant="h4" sx={{ fontWeight: 700 }} gutterBottom>
                    Translation Analytics Dashboard
                </Typography>
                <Typography variant="body1" sx={{ maxWidth: 640, opacity: 0.85 }} gutterBottom>
                    Monitor productivity, track language demand, and keep tabs on the health of your translation projects
                    at a glance. Configure automated reports to stay informed even when you are away from the dashboard.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
                    <Chip
                        icon={<SpeedIcon />}
                        label={`Average progress ${summaryStats.averageProgress}%`}
                        sx={{
                            bgcolor: alpha(theme.palette.common.white, 0.15),
                            color: theme.palette.common.white,
                            borderRadius: 2,
                            px: 1.5,
                            '& .MuiChip-icon': { color: theme.palette.common.white }
                        }}
                    />
                    <Chip
                        icon={<TrendingUpIcon />}
                        label={`${summaryStats.averageWords.toLocaleString()} words per project`}
                        sx={{
                            bgcolor: alpha(theme.palette.common.white, 0.15),
                            color: theme.palette.common.white,
                            borderRadius: 2,
                            px: 1.5,
                            '& .MuiChip-icon': { color: theme.palette.common.white }
                        }}
                    />
                    <Chip
                        icon={<TimelineIcon />}
                        label={`Last month ${latestMonthWords.toLocaleString()} words`}
                        sx={{
                            bgcolor: alpha(theme.palette.common.white, 0.15),
                            color: theme.palette.common.white,
                            borderRadius: 2,
                            px: 1.5,
                            '& .MuiChip-icon': { color: theme.palette.common.white }
                        }}
                    />
                </Stack>
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12} md={5}>
                    <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Stack>
                                <Typography variant="h6">Overview filters</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    These filters update the summary cards and project insights.
                                </Typography>
                            </Stack>
                            <Button startIcon={<FilterListIcon />} size="small" onClick={resetSummaryFilters}>
                                Reset
                            </Button>
                        </Stack>
                        <Stack spacing={2}>
                            <ToggleButtonGroup
                                size="small"
                                exclusive
                                value={summaryFilters.status}
                                onChange={(event, value) => {
                                    if (value !== null) {
                                        setSummaryFilters((prev) => ({ ...prev, status: value }));
                                    }
                                }}
                            >
                                <ToggleButton value="all">All statuses</ToggleButton>
                                <ToggleButton value="completed">Completed</ToggleButton>
                                <ToggleButton value="in-progress">In progress</ToggleButton>
                                <ToggleButton value="pending">Pending</ToggleButton>
                            </ToggleButtonGroup>
                            <FormControl fullWidth>
                                <InputLabel id="summary-time-range-label">Time range</InputLabel>
                                <Select
                                    labelId="summary-time-range-label"
                                    label="Time range"
                                    value={summaryFilters.timeRange}
                                    onChange={(event) =>
                                        setSummaryFilters((prev) => ({ ...prev, timeRange: event.target.value }))
                                    }
                                >
                                    {summaryTimeRangeOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Stack direction="row" spacing={1} flexWrap="wrap">
                                <Chip label={`Status: ${summaryFilters.status === 'all' ? 'All' : STATUS_LABELS[summaryFilters.status]}`} />
                                <Chip label={`Range: ${describeTimeRange(summaryFilters.timeRange)}`} />
                                <Chip label={`Projects: ${summaryStats.total}`} color="primary" variant="outlined" />
                            </Stack>
                        </Stack>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={7}>
                    <Paper component="form" onSubmit={handleReportSave} sx={{ p: 3, borderRadius: 3, height: '100%' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Stack spacing={0.5}>
                                <Typography variant="h6">Automated reporting</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Configure recurring email summaries tailored to the way you track progress.
                                </Typography>
                            </Stack>
                            <ScheduleIcon color="primary" />
                        </Stack>
                        <Stack spacing={2}>
                            <ToggleButtonGroup
                                value={reportConfig.frequency}
                                exclusive
                                onChange={(event, value) => {
                                    if (value) {
                                        setReportConfig((prev) => ({ ...prev, frequency: value }));
                                    }
                                }}
                                size="small"
                            >
                                {Object.keys(frequencyLabels).map((frequency) => (
                                    <ToggleButton key={frequency} value={frequency}>
                                        {frequencyLabels[frequency]}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <InputLabel id="report-delivery-label">Delivery cadence</InputLabel>
                                        <Select
                                            labelId="report-delivery-label"
                                            label="Delivery cadence"
                                            value={reportConfig.deliveryOption}
                                            onChange={(event) =>
                                                setReportConfig((prev) => ({ ...prev, deliveryOption: event.target.value }))
                                            }
                                        >
                                            {(frequencyDeliveryOptions[reportConfig.frequency] || []).map((option) => (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Send at"
                                        type="time"
                                        value={reportConfig.sendTime}
                                        onChange={(event) =>
                                            setReportConfig((prev) => ({ ...prev, sendTime: event.target.value }))
                                        }
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                            </Grid>
                            <TextField
                                label="Recipients"
                                placeholder="team@example.com, you@example.com"
                                value={reportConfig.recipients}
                                onChange={(event) =>
                                    setReportConfig((prev) => ({ ...prev, recipients: event.target.value }))
                                }
                                fullWidth
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <EmailOutlinedIcon sx={{ color: 'text.disabled' }} />
                                        </InputAdornment>
                                    )
                                }}
                            />
                            <FormHelperText>{recipientCount} recipient{recipientCount === 1 ? '' : 's'} will receive the report.</FormHelperText>
                            <TextField
                                label="Optional message"
                                placeholder="Add context or a reminder to the report email"
                                multiline
                                minRows={2}
                                value={reportConfig.customMessage}
                                onChange={(event) =>
                                    setReportConfig((prev) => ({ ...prev, customMessage: event.target.value }))
                                }
                            />
                            <Divider />
                            <Typography variant="subtitle2" color="text.secondary">
                                Include sections
                            </Typography>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap">
                                {sectionsForReport.map((section) => (
                                    <FormControlLabel
                                        key={section.key}
                                        control={
                                            <Switch
                                                checked={Boolean(reportConfig.includeSections[section.key])}
                                                onChange={(event) =>
                                                    setReportConfig((prev) => ({
                                                        ...prev,
                                                        includeSections: {
                                                            ...prev.includeSections,
                                                            [section.key]: event.target.checked
                                                        }
                                                    }))
                                                }
                                            />
                                        }
                                        label={section.label}
                                    />
                                ))}
                            </Stack>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={reportConfig.includeFilters}
                                            onChange={(event) =>
                                                setReportConfig((prev) => ({ ...prev, includeFilters: event.target.checked }))
                                            }
                                        />
                                    }
                                    label="Include active filters in email"
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={reportConfig.attachCsv}
                                            onChange={(event) =>
                                                setReportConfig((prev) => ({ ...prev, attachCsv: event.target.checked }))
                                            }
                                        />
                                    }
                                    label="Attach CSV snapshot"
                                />
                            </Stack>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
                                <InfoOutlinedIcon color="primary" />
                                <Typography variant="body2" color="text.secondary">
                                    {scheduleSummary}
                                </Typography>
                            </Stack>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    disabled={savingReport}
                                >
                                    {savingReport ? 'Saving...' : 'Save report settings'}
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => {
                                        setReportConfig({
                                            frequency: 'weekly',
                                            deliveryOption: frequencyDeliveryOptions.weekly[0].value,
                                            sendTime: '09:00',
                                            recipients: '',
                                            includeSections: {
                                                summary: true,
                                                volume: true,
                                                status: true,
                                                languages: true,
                                                insights: true,
                                                table: true
                                            },
                                            includeFilters: true,
                                            attachCsv: true,
                                            customMessage: ''
                                        });
                                        setReportFeedback(null);
                                    }}
                                >
                                    Restore defaults
                                </Button>
                            </Stack>
                            {reportFeedback && (
                                <Alert severity={reportFeedback.severity}>{reportFeedback.message}</Alert>
                            )}
                        </Stack>
                    </Paper>
                </Grid>
            </Grid>

            <Grid container spacing={3} sx={{ mt: 0.5 }}>
                {summaryCards.map((card) => (
                    <Grid item xs={12} sm={6} md={3} key={card.key}>
                        <Paper
                            sx={{
                                p: 3,
                                borderRadius: 3,
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                                boxShadow: '0 16px 40px rgba(16, 24, 40, 0.08)'
                            }}
                        >
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar
                                    variant="rounded"
                                    sx={{
                                        bgcolor: alpha(card.color, 0.16),
                                        color: card.color,
                                        width: 56,
                                        height: 56
                                    }}
                                >
                                    {card.icon}
                                </Avatar>
                                <Box>
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Typography variant="subtitle2" color="text.secondary">
                                            {card.label}
                                        </Typography>
                                        {card.isGlobal && <Chip size="small" label="Global" variant="outlined" />}
                                    </Stack>
                                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                        {typeof card.value === 'number'
                                            ? `${card.value.toLocaleString()}${card.valueSuffix || ''}`
                                            : card.value}
                                    </Typography>
                                </Box>
                            </Stack>
                            {card.sublabel && (
                                <Typography variant="body2" color="text.secondary">
                                    {card.sublabel}
                                </Typography>
                            )}
                        </Paper>
                    </Grid>
                ))}
            </Grid>

            <Grid container spacing={3} sx={{ mt: 0.5 }}>
                <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 3 }}>
                            <Box>
                                <Typography variant="h6">Monthly translation volume</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Track how many words were translated each month.
                                </Typography>
                            </Box>
                            <ToggleButtonGroup
                                size="small"
                                exclusive
                                value={volumeFilters.range}
                                onChange={(event, value) => {
                                    if (value) {
                                        setVolumeFilters({ range: value });
                                    }
                                }}
                            >
                                {volumeRangeOptions.map((option) => (
                                    <ToggleButton key={option.value} value={option.value}>
                                        {option.label}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                        </Stack>
                        {monthlyWordData.length === 0 || monthlyTrendMax === 0 ? (
                            <Typography color="text.secondary">No activity for the selected period.</Typography>
                        ) : (
                            <Box sx={{ position: 'relative', width: '100%', height: 240 }}>
                                <svg width="100%" height="100%" viewBox="0 0 640 240" preserveAspectRatio="none">
                                    <defs>
                                        <linearGradient id="monthlyArea" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor={alpha(theme.palette.primary.main, 0.4)} />
                                            <stop offset="100%" stopColor={alpha(theme.palette.primary.main, 0)} />
                                        </linearGradient>
                                    </defs>
                                    <polyline
                                        fill="url(#monthlyArea)"
                                        stroke="none"
                                        points={(() => {
                                            const paddingX = 56;
                                            const paddingY = 32;
                                            const width = 640;
                                            const height = 240;
                                            const availableWidth = width - paddingX * 2;
                                            const availableHeight = height - paddingY * 2;
                                            const points = monthlyWordData.map((entry, index) => {
                                                const fraction = monthlyWordData.length === 1 ? 0.5 : index / (monthlyWordData.length - 1);
                                                const x = paddingX + fraction * availableWidth;
                                                const valueRatio = entry.value / monthlyTrendMax;
                                                const y = height - paddingY - valueRatio * availableHeight;
                                                return `${x},${y}`;
                                            });
                                            if (!points.length) {
                                                return '';
                                            }
                                            const firstPoint = points[0].split(',');
                                            const lastPoint = points[points.length - 1].split(',');
                                            return [
                                                `${firstPoint[0]},${height - paddingY}`,
                                                ...points,
                                                `${lastPoint[0]},${height - paddingY}`
                                            ].join(' ');
                                        })()}
                                    />
                                    <polyline
                                        fill="none"
                                        stroke={theme.palette.primary.main}
                                        strokeWidth={3}
                                        strokeLinejoin="round"
                                        strokeLinecap="round"
                                        points={(() => {
                                            const paddingX = 56;
                                            const paddingY = 32;
                                            const width = 640;
                                            const height = 240;
                                            const availableWidth = width - paddingX * 2;
                                            const availableHeight = height - paddingY * 2;
                                            return monthlyWordData
                                                .map((entry, index) => {
                                                    const fraction = monthlyWordData.length === 1 ? 0.5 : index / (monthlyWordData.length - 1);
                                                    const x = paddingX + fraction * availableWidth;
                                                    const valueRatio = entry.value / monthlyTrendMax;
                                                    const y = height - paddingY - valueRatio * availableHeight;
                                                    return `${x},${y}`;
                                                })
                                                .join(' ');
                                        })()}
                                    />
                                    {monthlyWordData.map((entry, index) => {
                                        const paddingX = 56;
                                        const paddingY = 32;
                                        const width = 640;
                                        const height = 240;
                                        const availableWidth = width - paddingX * 2;
                                        const availableHeight = height - paddingY * 2;
                                        const fraction = monthlyWordData.length === 1 ? 0.5 : index / (monthlyWordData.length - 1);
                                        const x = paddingX + fraction * availableWidth;
                                        const valueRatio = entry.value / monthlyTrendMax;
                                        const y = height - paddingY - valueRatio * availableHeight;
                                        return (
                                            <g key={entry.label}>
                                                <circle cx={x} cy={y} r={5} fill={theme.palette.primary.main} />
                                                <text
                                                    x={x}
                                                    y={height - 12}
                                                    textAnchor="middle"
                                                    fontSize={12}
                                                    fill={theme.palette.text.secondary}
                                                >
                                                    {entry.label}
                                                </text>
                                                <text
                                                    x={x}
                                                    y={y - 12}
                                                    textAnchor="middle"
                                                    fontSize={12}
                                                    fill={theme.palette.primary.dark}
                                                >
                                                    {entry.value.toLocaleString()}
                                                </text>
                                            </g>
                                        );
                                    })}
                                </svg>
                            </Box>
                        )}
                    </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="h6">Project status breakdown</Typography>
                            <Tooltip title="Toggle which project states to include">
                                <IconButton size="small" onClick={resetStatusFilters}>
                                    <FilterListIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                        <Stack spacing={2} sx={{ mb: 2 }}>
                            <ToggleButtonGroup
                                size="small"
                                value={statusSectionFilters.statuses.length ? statusSectionFilters.statuses : []}
                                onChange={(event, value) => {
                                    setStatusSectionFilters((prev) => ({ ...prev, statuses: Array.isArray(value) ? value : [] }));
                                }}
                            >
                                {STATUSES.map((status) => (
                                    <ToggleButton key={status} value={status}>
                                        {STATUS_LABELS[status]}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                            <FormControl fullWidth>
                                <InputLabel id="status-time-range-label">Time range</InputLabel>
                                <Select
                                    labelId="status-time-range-label"
                                    label="Time range"
                                    value={statusSectionFilters.timeRange}
                                    onChange={(event) =>
                                        setStatusSectionFilters((prev) => ({ ...prev, timeRange: event.target.value }))
                                    }
                                >
                                    {statusTimeRangeOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>
                        {statusBreakdown.totalCount === 0 ? (
                            <Typography color="text.secondary">No project activity for the selected filters.</Typography>
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                <Box
                                    sx={{
                                        width: 160,
                                        height: 160,
                                        borderRadius: '50%',
                                        background: statusBreakdown.gradient || theme.palette.grey[200],
                                        position: 'relative',
                                        flexShrink: 0
                                    }}
                                >
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            transform: 'translate(-50%, -50%)',
                                            width: 100,
                                            height: 100,
                                            borderRadius: '50%',
                                            bgcolor: theme.palette.background.paper,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            boxShadow: '0 8px 16px rgba(16,24,40,0.12)'
                                        }}
                                    >
                                        <Typography variant="subtitle2" color="text.secondary">
                                            Projects
                                        </Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                            {statusBreakdown.totalCount}
                                        </Typography>
                                    </Box>
                                </Box>
                                <Stack spacing={1.5}>
                                    {statusBreakdown.entries.map((entry) => (
                                        <Stack key={entry.status} direction="row" spacing={1} alignItems="center">
                                            <Box
                                                sx={{
                                                    width: 12,
                                                    height: 12,
                                                    borderRadius: '50%',
                                                    bgcolor: entry.color,
                                                    boxShadow: '0 0 0 2px rgba(255,255,255,0.6)'
                                                }}
                                            />
                                            <Typography variant="body2" sx={{ minWidth: 96 }}>
                                                {entry.label}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {entry.count} project{entry.count === 1 ? '' : 's'}
                                            </Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                            </Box>
                        )}
                    </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 2 }}>
                            <Typography variant="h6">Top languages</Typography>
                            <ToggleButtonGroup
                                size="small"
                                exclusive
                                value={String(languageFilter.limit)}
                                onChange={(event, value) => {
                                    if (value) {
                                        setLanguageFilter({ limit: Number(value) });
                                    }
                                }}
                            >
                                {[3, 5, 10].map((limit) => (
                                    <ToggleButton key={limit} value={String(limit)}>
                                        Top {limit}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                        </Stack>
                        {displayedLanguages.length === 0 ? (
                            <Typography color="text.secondary">No language data available.</Typography>
                        ) : (
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: 220 }}>
                                {displayedLanguages.map((lang) => {
                                    const ratio = maxLanguageCount ? (lang.count || 0) / maxLanguageCount : 0;
                                    return (
                                        <Box
                                            key={lang.name}
                                            sx={{
                                                flex: 1,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: 1
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    height: `${Math.max(ratio * 100, 6)}%`,
                                                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                                                    borderRadius: 2,
                                                    position: 'relative',
                                                    display: 'flex',
                                                    alignItems: 'flex-end',
                                                    justifyContent: 'center',
                                                    overflow: 'hidden',
                                                    boxShadow: 'inset 0 0 0 1px rgba(33, 150, 243, 0.12)'
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: '100%',
                                                        bgcolor: theme.palette.primary.main,
                                                        height: `${Math.max(ratio * 100 - 15, 12)}%`,
                                                        borderRadius: 2
                                                    }}
                                                />
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        position: 'absolute',
                                                        bottom: 8,
                                                        fontWeight: 600,
                                                        color: theme.palette.common.white
                                                    }}
                                                >
                                                    {lang.count}
                                                </Typography>
                                            </Box>
                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                <Chip icon={<LanguageIcon />} label="Demand" variant="outlined" size="small" />
                                                <Typography variant="body2" color="text.secondary" align="center">
                                                    {lang.name}
                                                </Typography>
                                            </Stack>
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}
                    </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 3, borderRadius: 3, height: '100%' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="h6">Project insights</Typography>
                            <Stack direction="row" spacing={1}>
                                <Chip label={STATUS_LABELS[summaryFilters.status] || 'All statuses'} size="small" />
                                <Chip label={describeTimeRange(summaryFilters.timeRange)} size="small" variant="outlined" />
                            </Stack>
                        </Stack>
                        <Stack spacing={2}>
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Average progress
                                </Typography>
                                <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 1 }}>
                                    <Box sx={{ flexGrow: 1 }}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={summaryStats.averageProgress}
                                            sx={{ height: 10, borderRadius: 5 }}
                                        />
                                    </Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {summaryStats.averageProgress}%
                                    </Typography>
                                </Stack>
                            </Box>
                            <Divider />
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Average words per project
                                </Typography>
                                <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 600 }}>
                                    {summaryStats.averageWords.toLocaleString()}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Calculated across {summaryStats.total}{' '}
                                    {summaryStats.total === 1 ? 'project' : 'projects'} matching the active filters.
                                </Typography>
                            </Box>
                            <Divider />
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Total words processed
                                </Typography>
                                <Typography variant="h6" sx={{ mt: 0.5, fontWeight: 600 }}>
                                    {summaryStats.totalWords.toLocaleString()}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Sum of all section word counts for tracked projects in scope.
                                </Typography>
                            </Box>
                        </Stack>
                    </Paper>
                </Grid>
            </Grid>

            <Grid container spacing={3} sx={{ mt: 0.5 }}>
                <Grid item xs={12}>
                    <Paper sx={{ p: 3, borderRadius: 3 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 3 }}>
                            <Box>
                                <Typography variant="h6">Project progress</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Slice and dice project activity, then export the current view for stakeholders.
                                </Typography>
                            </Box>
                            <Stack direction="row" spacing={1}>
                                <Button variant="outlined" startIcon={<FilterListIcon />} onClick={resetTableFilters}>
                                    Clear filters
                                </Button>
                                <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportTable}>
                                    Export CSV
                                </Button>
                            </Stack>
                        </Stack>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    label="Search projects"
                                    placeholder="Search by project name"
                                    value={tableFilters.search}
                                    onChange={(event) =>
                                        setTableFilters((prev) => ({ ...prev, search: event.target.value }))
                                    }
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth>
                                    <InputLabel id="table-status-label">Status</InputLabel>
                                    <Select
                                        labelId="table-status-label"
                                        label="Status"
                                        value={tableFilters.status}
                                        onChange={(event) =>
                                            setTableFilters((prev) => ({ ...prev, status: event.target.value }))
                                        }
                                    >
                                        <MenuItem value="all">All statuses</MenuItem>
                                        {STATUSES.map((status) => (
                                            <MenuItem key={status} value={status}>
                                                {STATUS_LABELS[status]}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <FormControl fullWidth>
                                    <InputLabel id="table-time-range-label">Time range</InputLabel>
                                    <Select
                                        labelId="table-time-range-label"
                                        label="Time range"
                                        value={tableFilters.timeRange}
                                        onChange={(event) =>
                                            setTableFilters((prev) => ({ ...prev, timeRange: event.target.value }))
                                        }
                                    >
                                        {tableTimeRangeOptions.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <ToggleButtonGroup
                                    size="small"
                                    exclusive
                                    value={tableFilters.sort}
                                    onChange={(event, value) => {
                                        if (value) {
                                            setTableFilters((prev) => ({ ...prev, sort: value }));
                                        }
                                    }}
                                    fullWidth
                                >
                                    {tableSortOptions.map((option) => (
                                        <ToggleButton key={option.value} value={option.value}>
                                            {option.label}
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                            </Grid>
                        </Grid>
                        {tableProjects.length === 0 ? (
                            <Typography color="text.secondary">No projects match the current filters.</Typography>
                        ) : (
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Project</TableCell>
                                        <TableCell>Progress</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="right">Words</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {tableProjects.map((project) => {
                                        const chipColor = statusToChipColor[normalizeStatus(project.status)] || 'default';
                                        return (
                                            <TableRow key={project.id} hover>
                                                <TableCell>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                        {project.name || 'Untitled project'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {project.timestamp
                                                            ? new Date(project.timestamp).toLocaleDateString()
                                                            : 'No date available'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ minWidth: 200 }}>
                                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={project.progress}
                                                            sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                                                        />
                                                        <Typography variant="body2">{project.progress}%</Typography>
                                                    </Stack>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        size="small"
                                                        label={STATUS_LABELS[normalizeStatus(project.status)]}
                                                        color={chipColor}
                                                        sx={{ textTransform: 'capitalize' }}
                                                    />
                                                </TableCell>
                                                <TableCell align="right">{project.wordCount.toLocaleString()}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
}
